import { useEffect, useState } from 'react';
import { Landmark, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useI18n } from '@/hooks/useI18n';
import { toast } from '@/hooks/useToast';
import { usePaymentDefaults, paymentOptionsErrorKey } from '@/lib/payments/hooks/usePaymentOptions';
import type { TranslationKey } from '@/types/i18n.types';
import { IBankTransferDetails, isValidIban } from '@/lib/payments/models/payment-options.model';

type BankForm = Record<keyof IBankTransferDetails, string>;

const EMPTY: BankForm = {
    bank_account_holder: '',
    bank_iban: '',
    bank_bic: '',
    bank_name: '',
    bank_reference: '',
    bank_instructions: '',
    bank_receipt_note: '',
};

/**
 * Coordinate bancarie predefinite di chi incassa: precompilano la sezione bonifico a ogni
 * invio di proposta. Sta nella tab "Metodi di pagamento" accanto alla card Stripe, con gli
 * stessi ruoli — il membro del team usa quelle dell'agenzia e le cambia, se serve, sulla
 * singola proposta.
 */
const BankTransferDefaultsCard = () => {
    const { t } = useI18n();
    const { defaults, isLoading, save, isSaving } = usePaymentDefaults();

    const [form, setForm] = useState<BankForm>(EMPTY);
    const [ibanError, setIbanError] = useState(false);

    useEffect(() => {
        if (!defaults) return;
        setForm(Object.fromEntries(
            (Object.keys(EMPTY) as (keyof BankForm)[]).map((k) => [k, defaults[k] ?? '']),
        ) as BankForm);
    }, [defaults]);

    const setField = (field: keyof BankForm, value: string) => {
        setForm((f) => ({ ...f, [field]: value }));
        if (field === 'bank_iban') setIbanError(false);
    };

    const handleSave = async () => {
        if (form.bank_iban.trim() && !isValidIban(form.bank_iban)) {
            setIbanError(true);
            return;
        }
        try {
            await save(form);
            toast({ variant: 'success', title: t('paymentOptions.settings.saved') });
        } catch (error) {
            toast({ title: t(paymentOptionsErrorKey(error)), variant: 'destructive' });
        }
    };

    const field = (key: keyof BankForm, labelKey: TranslationKey, optional = false) => (
        <div className="space-y-1">
            <Label htmlFor={`defaults-${key}`} className="text-xs">
                {t(labelKey)}
                {optional && <span className="text-gray-400 font-normal"> ({t('paymentOptions.bank.optional')})</span>}
            </Label>
            <Input
                id={`defaults-${key}`}
                value={form[key]}
                onChange={(e) => setField(key, e.target.value)}
                hasError={key === 'bank_iban' && ibanError}
                className="h-9"
            />
            {key === 'bank_iban' && ibanError && (
                <p className="text-xs text-yellow-700">{t('paymentOptions.bank.invalidIban')}</p>
            )}
        </div>
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-xl font-normal flex items-center gap-2">
                    <Landmark className="h-4 w-4" />
                    {t('paymentOptions.settings.title')}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <p className="text-sm text-gray-500">{t('paymentOptions.settings.description')}</p>

                {isLoading ? (
                    <div className="flex justify-center py-6">
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {field('bank_account_holder', 'paymentOptions.bank.accountHolder')}
                            {field('bank_iban', 'paymentOptions.bank.iban')}
                            {field('bank_bic', 'paymentOptions.bank.bic', true)}
                            {field('bank_name', 'paymentOptions.bank.bankName', true)}
                        </div>
                        {field('bank_reference', 'paymentOptions.bank.reference', true)}

                        <div className="space-y-1">
                            <Label htmlFor="defaults-instructions" className="text-xs">
                                {t('paymentOptions.bank.instructions')}
                                <span className="text-gray-400 font-normal"> ({t('paymentOptions.bank.optional')})</span>
                            </Label>
                            <Textarea
                                id="defaults-instructions"
                                rows={2}
                                value={form.bank_instructions}
                                placeholder={t('paymentOptions.bank.instructionsPlaceholder')}
                                onChange={(e) => setField('bank_instructions', e.target.value)}
                            />
                        </div>

                        <div className="space-y-1">
                            <Label htmlFor="defaults-receipt-note" className="text-xs">
                                {t('paymentOptions.bank.receiptNote')}
                                <span className="text-gray-400 font-normal"> ({t('paymentOptions.bank.optional')})</span>
                            </Label>
                            <Textarea
                                id="defaults-receipt-note"
                                rows={3}
                                value={form.bank_receipt_note}
                                placeholder={t('paymentOptions.bank.defaultReceiptNote')}
                                onChange={(e) => setField('bank_receipt_note', e.target.value)}
                            />
                        </div>

                        <div className="flex justify-end">
                            <Button size="sm" onClick={handleSave} disabled={isSaving}>
                                {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                                {t('common.save')}
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default BankTransferDefaultsCard;
